import React, { useState, useEffect, useMemo, useCallback } from 'react';
import MatchListing from './MatchListing';
import { MATCH_STATUS, type Match } from '../../types/match';
import { useNetworkMatches } from '../../hooks/useMatch';

// REC-487: Storage key for tracking when matches are being generated
const FINDING_MATCHES_KEY = 'finding_matches_started_at';
const FINDING_MATCHES_TIMEOUT_MS = 120000; // 2 minutes max wait
const FINDING_MATCHES_POLL_INTERVAL_MS = 5000; // Poll every 5 seconds

interface PendingMatchesProps {
  onDataChange?: (count: number) => void;
  startDate?: Date | null;
  /** Multi-event: scope to one event; null = all events. */
  eventId?: string | null;
  endDate?: Date | null;
  search: string;
  /** Multi-event: event-aware empty-state copy from the parent. */
  emptyMessage?: string;
}

const PendingMatches = ({
  startDate,
  eventId,
  endDate,
  search,
  emptyMessage,
}: PendingMatchesProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(21);
  const [isFindingMatches, setIsFindingMatches] = useState(false);
  const {
    data: matches,
    isLoading,
    refetch: fetchNetworkMatches,
  } = useNetworkMatches({
    page: currentPage,
    limit,
    status: MATCH_STATUS.PENDING,
    start_date: startDate?.toISOString() ?? undefined,
    event_id: eventId ?? undefined,
    end_date: endDate?.toISOString() ?? undefined,
  });

  // REC-487: Check if we're in "finding matches" state (just completed onboarding)
  useEffect(() => {
    const findingMatchesStarted = sessionStorage.getItem(FINDING_MATCHES_KEY);
    if (findingMatchesStarted) {
      const startTime = parseInt(findingMatchesStarted, 10);
      const elapsed = Date.now() - startTime;

      if (elapsed < FINDING_MATCHES_TIMEOUT_MS) {
        setIsFindingMatches(true);
      } else {
        // Timeout expired, clear flag
        sessionStorage.removeItem(FINDING_MATCHES_KEY);
        setIsFindingMatches(false);
      }
    }
  }, []);

  // REC-487: Auto-poll while finding matches
  useEffect(() => {
    if (!isFindingMatches) return;

    const matchCount = matches?.result?.items?.length ?? 0;

    // If matches found, stop polling and clear flag
    if (matchCount > 0) {
      sessionStorage.removeItem(FINDING_MATCHES_KEY);
      setIsFindingMatches(false);
      return;
    }

    // Check timeout
    const findingMatchesStarted = sessionStorage.getItem(FINDING_MATCHES_KEY);
    if (findingMatchesStarted) {
      const startTime = parseInt(findingMatchesStarted, 10);
      const elapsed = Date.now() - startTime;

      if (elapsed >= FINDING_MATCHES_TIMEOUT_MS) {
        sessionStorage.removeItem(FINDING_MATCHES_KEY);
        setIsFindingMatches(false);
        return;
      }
    }

    // Poll for matches
    const pollInterval = setInterval(() => {
      fetchNetworkMatches();
    }, FINDING_MATCHES_POLL_INTERVAL_MS);

    return () => clearInterval(pollInterval);
  }, [isFindingMatches, matches, fetchNetworkMatches]);

  const filteredMatches = useMemo(() => {
    if (search.length > 2) {
      const searchLower = search.toLowerCase();
      return matches?.result.items.filter((match: Match) => {
        const name = match.other_user?.name ?? '';
        const objective = match.other_user?.objective ?? '';
        return (
          name.toLowerCase().includes(searchLower) ||
          objective.toLowerCase().includes(searchLower)
        );
      });
    }
    return matches?.result.items;
  }, [search, matches]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    if (endDate && currentPage !== 1) {
      setCurrentPage(1);
    } else {
      fetchNetworkMatches();
    }
  }, [endDate, currentPage]);

  return (
    <MatchListing
      matches={filteredMatches}
      currentPage={currentPage}
      totalPages={matches?.result.meta.pages ?? 0}
      totalItems={matches?.result.meta.total ?? 0}
      limit={limit}
      onPageChange={handlePageChange}
      loading={isLoading}
      isFindingMatches={isFindingMatches}
      emptyMessage={emptyMessage}
    />
  );
};

export default PendingMatches;
